(ns app.front.view.permit.draft-actions
  (:require [app.domain.verdict.model :as verdict-model]
            [app.front.entity-edit :as ee]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.error-list :as ui-error-list]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.permit.permit-number-editor :as permit-number-editor]
            [app.front.view.workspace.subs :as ws-subs]
            [app.process.model :as process-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(defmulti ^{:arglists '([ctx section])} issue-warning
  "Optional warning rendered from a top-level section on the issue permit confirm modal"
  (fn [_ section] (:verdict.section/type section)))

(defmethod issue-warning :default [_ _] nil)

(defmethod issue-warning :verdict.section.type/attachments
  [{:keys [tr]} section]
  (when-not (-> section :verdict.section/content seq)
    [:div (tr [:permit/attachment-missing-confirm])]))

(defn- get-problems-for-signal [signal]
  (->> signal
       (process-model/rejected-by-guards (<sub [::process/application-process]))
       (map (fn [guard] [guard nil]))))

(rf/reg-sub ::updates-in-progress?
  ;; all signals that may be triggered from the permit form - if this becomes too unwieldy, a general
  ;; "any signal processing?" might do the trick too
  :<- [::process/signal-processing?
       :process.signal/update-verdict-draft
       :process.signal/set-verdict-issue-date
       :process.signal/update-inspection
       :process.signal/add-inspection
       :process.signal/remove-inspection
       :process.signal/edit-permit-number
       :process.signal/link-renewal-to-licence]
  :<- [::ee/has-any-unsaved?]
  (fn [[saving? has-edits?] _]
    (or saving? has-edits?)))

(rf/reg-sub ::audit-additional-information
  :-> ::audit-additional-information)

(rf/reg-event-db ::set-audit-additional-information
  (fn [db [_ msg]]
    (assoc db ::audit-additional-information msg)))

(defn draft-actions [{:keys [tr application verdict] :as ctx} sections]
  (r/with-let [on-delete-fn (futil/make-callback-fn (fn [application-id delete-signal]
                                                      (>evt [::process/signal application-id delete-signal])))
               on-issue-fn  (futil/make-callback-fn (fn [application-id signal-kw permit-id audit-additional-information]
                                                      (>evt [::process/signal
                                                             application-id
                                                             [signal-kw (cond-> {:id permit-id}
                                                                          (and (= signal-kw :process.signal/issue-permit)
                                                                               (not (str/blank? audit-additional-information)))
                                                                          (merge {:audit-additional-information audit-additional-information}))]])))]
    (let [permit-id                    (:db/id verdict)
          application-id               (:db/id application)
          issue-signal                 [:process.signal/issue-permit {:id permit-id}]
          set-ready-signal             [:process.signal/set-permit-ready {:id permit-id}]
          delete-signal                [:process.signal/delete-verdict-draft {:id permit-id}]
          signal-kw                    (cond
                                         (<sub [::process/app-signal-permitted? issue-signal])
                                         :process.signal/issue-permit
                                         (<sub [::process/app-signal-permitted? set-ready-signal])
                                         :process.signal/set-permit-ready
                                         :else
                                         nil)
          forbidden?                   (and (<sub [::process/signal-forbidden? application-id issue-signal])
                                            (<sub [::process/signal-forbidden? application-id set-ready-signal]))
          disabled?                    (or (and (not (<sub [::process/app-signal-permitted? issue-signal]))
                                                (not (<sub [::process/app-signal-permitted? set-ready-signal])))
                                           (<sub [::permit-number-editor/unsaved-changes? permit-id]))
          can-update?                  (<sub [::process/app-signal-permitted? [:process.signal/update-verdict-draft]])
          saving?                      (<sub [::updates-in-progress?]) ;; do not allow issuing while save commands are being processed
          issuing?                     (= signal-kw :process.signal/issue-permit)
          valid-until                  (->> (verdict-model/find-section verdict {:verdict.section/id :valid-until-date})
                                            :verdict.section/content)
          expired?                     (boolean (when (inst? valid-until)
                                                  (date-util/after? (date-util/now) valid-until)))
          audit-additional-information (<sub [::audit-additional-information])]
      [:div.flex.flex-col
       (when-let [problems (->> (vector (get-problems-for-signal issue-signal)
                                        (get-problems-for-signal set-ready-signal))
                                (filter not-empty)
                                (sort-by count)
                                first)]
         (when (and can-update?
                    (or disabled? forbidden?))
           [:div.mb-4.ml-8.self-end
            [ui-error-list/signal-error-list tr problems]]))
       [:div.verdict-page__buttons.mt-2 {}
        (when (<sub [::process/app-signal-permitted? delete-signal])
          [ui/modal-confirmation-button
           {:tr                  tr
            :dangerous?          true
            :id                  "delete-verdict-draft"
            :yes-button-text     (tr [:delete])
            :trigger-button-opts {:kind :danger
                                  :icon i/trash}
            :trigger-button-text (tr [:verdict/delete])
            :on-yes-click        (on-delete-fn application-id delete-signal)}
           [:div (tr [:permit/delete-confirm])]])
        (when (not forbidden?)
          (if (<sub [::ws-subs/loading-config?])
            (cc/button-skeleton)
            [ui/modal-confirmation-button
             {:tr                  tr
              :disabled?           disabled?
              :yes-disabled?       (or saving?
                                       (and issuing?
                                            expired?
                                            (str/blank? audit-additional-information)))
              :id                  "issue-permit"
              :trigger-button-opts {:kind :primary
                                    :icon i/check}
              :trigger-button-text (tr [(or signal-kw :process.signal/issue-permit)])
              :on-yes-click        (on-issue-fn application-id signal-kw permit-id audit-additional-information)}
             [:<>
              (when (and issuing? expired?)
                [ui/text-area {:label-text (tr [:verdict/issue-expired])
                               :value      audit-additional-information
                               :on-change  #(>evt [::set-audit-additional-information (futil/tval %)])}])
              (for-frag [section sections]
                [issue-warning ctx section])
              (when saving?
                [:div.py-2
                 (cc/loading-spinner {:with-overlay false :small true})])]]))]])))
